/**
 * Analytics - Chats
 * Chat analytics with date range + AI-agent / assignee / channel filters.
 * Filters default to "All" (no param sent — backend returns unfiltered data).
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  useAnalytics,
  AnalyticsFilters,
  ChatMetrics,
  TimeSeriesData,
  CountryStats,
  AgentMessageBreakdown,
} from '@/hooks/useAnalytics';
import DateRangePicker from '@/components/analytics/DateRangePicker';
import MetricsCards from '@/components/analytics/MetricsCards';
import ChatStartedGraph from '@/components/analytics/ChatStartedGraph';
import ChatsByCountryMap from '@/components/analytics/ChatsByCountryMap';
import MessagesByAgentTable from '@/components/analytics/MessagesByAgentTable';
import adminApi from '@/services/admin.api';

const ChatsAnalytics: React.FC = () => {
  const {
    getChatMetrics,
    getChatStartedTimeSeries,
    getChatsByCountry,
    getMessagesByAgent,
    loading,
    error,
  } = useAnalytics();

  // Default to last week
  const getDefaultDateRange = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [metrics, setMetrics] = useState<ChatMetrics | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData | null>(null);
  const [countryStats, setCountryStats] = useState<CountryStats[]>([]);
  const [agentBreakdown, setAgentBreakdown] = useState<AgentMessageBreakdown[]>([]);
  const [unattributedMessages, setUnattributedMessages] = useState(0);

  // Filters — '' means "All" (param omitted → backend unfiltered)
  const [agentId, setAgentId] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [channel, setChannel] = useState<'' | 'whatsapp' | 'instagram'>('');

  // Dropdown options
  const [botAgents, setBotAgents] = useState<Array<{ _id: string; name: string }>>([]);
  const [teamMembers, setTeamMembers] = useState<Array<{ _id: string; name: string }>>([]);

  useEffect(() => {
    // AI agents — the filter value is the agent's _id (matched at the
    // message level against WhatsAppMessage.metadata.selectedAgent, which is
    // where that field's real, per-response value is stored).
    adminApi
      .getAgents({ isActive: true }, { suppressPermissionLock: true })
      .then(({ data }) => setBotAgents((data || []).map((a: any) => ({ _id: a._id, name: a.name }))))
      .catch(() => {});
    adminApi
      .getTeamDirectory({ suppressPermissionLock: true })
      .then((list) =>
        setTeamMembers(
          (list || [])
            .filter((m) => m.isActive)
            .map((m) => ({ _id: m._id, name: `${m.firstName} ${m.lastName}`.trim() || m.email }))
        )
      )
      .catch(() => {});
  }, []);

  // Fetch all analytics data when date range or any filter changes
  useEffect(() => {
    const filters: AnalyticsFilters = {
      ...(agentId ? { agentId } : {}),
      ...(assignedTo ? { assignedTo } : {}),
      ...(channel ? { channel } : {}),
    };
    const fetchAnalytics = async () => {
      const [metricsData, timeSeriesDataResult, countryData, agentData] = await Promise.all([
        getChatMetrics(dateRange.start, dateRange.end, filters),
        getChatStartedTimeSeries(dateRange.start, dateRange.end, filters),
        getChatsByCountry(dateRange.start, dateRange.end, filters),
        // agentId is deliberately not passed through here — it's the
        // dimension this call breaks down BY, not a way to narrow it.
        getMessagesByAgent(dateRange.start, dateRange.end, filters),
      ]);

      if (metricsData) setMetrics(metricsData);
      if (timeSeriesDataResult) setTimeSeriesData(timeSeriesDataResult);
      if (countryData) setCountryStats(countryData.countries);
      if (agentData) {
        setAgentBreakdown(agentData.agents);
        setUnattributedMessages(agentData.unattributedMessages);
      }
    };

    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end, agentId, assignedTo, channel]);

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setDateRange({ start: startDate, end: endDate });
  };

  const agentNameById = useMemo(
    () => Object.fromEntries(botAgents.map((a) => [a._id, a.name])),
    [botAgents]
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight="bold">
          Chat Analytics
        </Typography>
        <DateRangePicker
          startDate={dateRange.start}
          endDate={dateRange.end}
          onChange={handleDateRangeChange}
        />
      </Box>

      {/* Filters — All by default; combine as AND */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>AI Agent</InputLabel>
          <Select value={agentId} label="AI Agent" onChange={(e) => setAgentId(e.target.value)}>
            <MenuItem value="">All agents</MenuItem>
            {botAgents.map((a) => (
              <MenuItem key={a._id} value={a._id}>
                {a.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Assigned To</InputLabel>
          <Select
            value={assignedTo}
            label="Assigned To"
            onChange={(e) => setAssignedTo(e.target.value)}
          >
            <MenuItem value="">Anyone</MenuItem>
            {teamMembers.map((m) => (
              <MenuItem key={m._id} value={m._id}>
                {m.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Channel</InputLabel>
          <Select
            value={channel}
            label="Channel"
            onChange={(e) => setChannel(e.target.value as '' | 'whatsapp' | 'instagram')}
          >
            <MenuItem value="">All channels</MenuItem>
            <MenuItem value="whatsapp">WhatsApp</MenuItem>
            <MenuItem value="instagram">Instagram</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Metrics Cards */}
      <Box mb={3}>
        <MetricsCards metrics={metrics} loading={loading} />
      </Box>

      {/* Charts Grid */}
      <Box display="flex" flexDirection="column" gap={3}>
        <ChatStartedGraph data={timeSeriesData} loading={loading} />
        <MessagesByAgentTable
          agents={agentBreakdown}
          unattributedMessages={unattributedMessages}
          agentNameById={agentNameById}
          loading={loading}
        />
        {/* customerCountryCode is only ever populated when a conversation's
            phone number resolves to a country (or a source explicitly
            supplied one) — a tenant/date-range with none yet shouldn't show
            an empty "no data" card, so this renders only once real data
            exists. Still shows through a loading fetch that follows a
            successful one (countryStats holds the prior result until the
            new one lands), which is the one case worth keeping visible. */}
        {countryStats.length > 0 && <ChatsByCountryMap countries={countryStats} loading={loading} />}
      </Box>
    </Box>
  );
};

export default ChatsAnalytics;
